import { appendRepositoryEvent, canonicalJson } from "./transaction.js";
export class SqliteRevisionedRepository {
    #database;
    #domain;
    #now;
    constructor(database, options) {
        this.#database = database;
        this.#domain = options.domain;
        this.#now = options.now ?? (() => new Date().toISOString());
    }
    async apply(entity) {
        validateEntity(entity);
        return this.#database.transaction("IMMEDIATE", (transaction) => {
            const current = this.#read(transaction, entity.id);
            if (current !== undefined && current.rev > entity.rev)
                return emptyWrite();
            if (current !== undefined && current.rev === entity.rev) {
                if (canonicalJson(current.value) !== canonicalJson(entity.value)) {
                    throw new Error(`Revision ${this.#domain}/${entity.id} has different values at the same revision`);
                }
                return emptyWrite();
            }
            const occurredAt = this.#now();
            this.#upsert(transaction, entity, occurredAt);
            const event = revisionedEvent(this.#domain, entity.id, current === undefined ? "created" : "updated", occurredAt);
            return { eventSequences: [appendRepositoryEvent(transaction, entity.id, event)], events: [event] };
        });
    }
    async command(input) {
        validateCommand(input);
        return this.#database.transaction("IMMEDIATE", (transaction) => {
            const commandRow = transaction.selectObject("SELECT result_json FROM client_revisioned_commands WHERE domain = ? AND idempotency_key = ?", [this.#domain, input.idempotencyKey]);
            if (commandRow !== undefined)
                return parseCommandResult(commandRow.result_json);
            const current = this.#read(transaction, input.id);
            if (current === undefined && input.baseRev !== 0) {
                throw new Error(`Cannot update missing ${this.#domain}/${input.id} from revision ${input.baseRev}`);
            }
            if (current !== undefined && current.rev !== input.baseRev) {
                const result = { status: "conflict", theirs: current };
                this.#storeCommandResult(transaction, input, result);
                return result;
            }
            const entity = {
                id: input.id,
                rev: input.baseRev + 1,
                value: input.value,
            };
            const occurredAt = this.#now();
            this.#upsert(transaction, entity, occurredAt);
            const event = revisionedEvent(this.#domain, entity.id, current === undefined ? "created" : "updated", occurredAt);
            appendRepositoryEvent(transaction, entity.id, event);
            const result = { entity, status: "accepted" };
            this.#storeCommandResult(transaction, input, result);
            return result;
        });
    }
    async list() {
        return this.#database.selectObjects("SELECT id, rev, value_json FROM client_revisioned_entities WHERE domain = ? ORDER BY id", [this.#domain]).map((row) => parseEntity(row));
    }
    async read(id) {
        if (id.trim() === "")
            throw new TypeError("Revision entity id must not be empty");
        const row = this.#database.selectObject("SELECT id, rev, value_json FROM client_revisioned_entities WHERE domain = ? AND id = ?", [this.#domain, id]);
        return row === undefined ? undefined : parseEntity(row);
    }
    #read(database, id) {
        const row = database.selectObject("SELECT id, rev, value_json FROM client_revisioned_entities WHERE domain = ? AND id = ?", [this.#domain, id]);
        return row === undefined ? undefined : parseEntity(row);
    }
    #storeCommandResult(database, input, result) {
        database.exec(`INSERT INTO client_revisioned_commands(domain, idempotency_key, entity_id, result_json, created_at)
       VALUES (?, ?, ?, ?, ?)`, { bind: [this.#domain, input.idempotencyKey, input.id, JSON.stringify(result), this.#now()] });
    }
    #upsert(database, entity, updatedAt) {
        database.exec(`INSERT INTO client_revisioned_entities(domain, id, rev, value_json, updated_at)
       VALUES (?, ?, ?, ?, ?)
       ON CONFLICT(domain, id) DO UPDATE SET
         rev = excluded.rev,
         value_json = excluded.value_json,
         updated_at = excluded.updated_at`, { bind: [this.#domain, entity.id, entity.rev, JSON.stringify(entity.value), updatedAt] });
    }
}
function emptyWrite() {
    return { eventSequences: [], events: [] };
}
function revisionedEvent(domain, entityId, action, occurredAt) {
    return {
        occurredAt,
        payload: {
            action,
            entityId,
            entityType: domain === "settings" ? "setting" : domain,
        },
        type: "domain.entity-changed",
    };
}
function validateEntity(entity) {
    if (entity.id.trim() === "" || !Number.isSafeInteger(entity.rev) || entity.rev < 0 || !isRepositoryJsonValue(entity.value)) {
        throw new TypeError("Invalid revisioned entity");
    }
}
function validateCommand(input) {
    if (input.id.trim() === "" || input.idempotencyKey.trim() === "" || !Number.isSafeInteger(input.baseRev) ||
        input.baseRev < 0 || !isRepositoryJsonValue(input.value))
        throw new TypeError("Invalid revision command");
}
function parseCommandResult(value) {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed))
        throw new TypeError("Invalid stored revision command");
    const record = parsed;
    if (record.status === "accepted")
        return { entity: parseEntity(record.entity), status: "accepted" };
    if (record.status === "conflict")
        return { status: "conflict", theirs: parseEntity(record.theirs) };
    throw new TypeError("Invalid stored revision command status");
}
function parseEntity(input) {
    if (input === null || typeof input !== "object" || Array.isArray(input))
        throw new TypeError("Invalid stored revisioned entity");
    const row = input;
    const value = typeof row.value_json === "string" ? JSON.parse(row.value_json) : row.value;
    if (typeof row.id !== "string" || !Number.isSafeInteger(row.rev) || row.rev < 0 || !isRepositoryJsonValue(value)) {
        throw new TypeError("Invalid stored revisioned entity");
    }
    return { id: row.id, rev: row.rev, value: value };
}
function isRepositoryJsonValue(value) {
    if (value === null || typeof value === "boolean" || typeof value === "string")
        return true;
    if (typeof value === "number")
        return Number.isFinite(value);
    if (Array.isArray(value))
        return value.every(isRepositoryJsonValue);
    if (typeof value !== "object")
        return false;
    return Object.values(value).every(isRepositoryJsonValue);
}
//# sourceMappingURL=sqlite-revisioned-repository.js.map